import { useState, useCallback, useRef } from "react";
import { ToolContainer, FileDropZone, ProcessingState, DownloadButton, downloadBlob } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { AlertTriangle, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { protectPdfWithPassword, verifyProtectedPdf, type ProtectCheck } from "./operations";
import { stemOfPdfName } from "./pdfMetadata";
import { isSkippedLocked } from "./unlockPdf";
import { protectedPdfNotice, UNLOCK_PDF_PATH } from "./protectedPdfNotice";
import { formatFileSize } from "@/lib/formatFileSize";
import { BatchRunner, BatchModeToggle } from "@/components/BatchRunner";
import { NoticeBox } from "@/components/NoticeBox";

const TOOL_INFO_CONTENT = {
  what: `Adds a password to a PDF with AES-256 encryption, the strongest standard the PDF format offers, so the file asks for the password before it opens in any current PDF reader. Your pages, bookmarks, form fields and links stay as they were. Everything happens in your browser; the file is never uploaded.`,
  how: `Drop a PDF, type the password twice and click Protect PDF. The tool then opens the protected file with your password to check that it works, and shows the result before you download. Optionally block printing, copying or editing (readers are asked to respect this; the password is what keeps people out). To protect several PDFs with one password, switch to Several files (up to 5 at a time on the trial, counted as one use).`,
  tip: `Write the password down somewhere safe: there is no way to recover it. Letters with accents and other alphabets work. Very old PDF readers from before 2006 may not open AES-256 files; every current reader, browser and phone does.`,
};

const COMMON = new Set(["password", "password1", "12345678", "123456789", "qwertyuiop", "letmein", "iloveyou", "admin123", "welcome1", "p@ssw0rd", "passw0rd", "11111111", "abc12345"]);

/** Length first, then variety; common passwords are always weak. */
function rate(pw: string): { label: string; color: string; pct: number } | null {
  if (!pw) return null;
  if (COMMON.has(pw.toLowerCase()) || /^(.)\1+$/.test(pw)) return { label: "Weak (very common)", color: "bg-red-500", pct: 15 };
  const kinds = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/].filter((r) => r.test(pw)).length;
  const bits = pw.length * Math.log2(Math.max(2, kinds * 12));
  if (pw.length < 8 || bits < 40) return { label: "Weak", color: "bg-red-500", pct: 25 };
  if (pw.length < 12 && bits < 60) return { label: "Medium", color: "bg-yellow-500", pct: 55 };
  if (bits < 75) return { label: "Good", color: "bg-lime-500", pct: 80 };
  return { label: "Strong", color: "bg-green-500", pct: 100 };
}

const MIN_LEN = 6;

export default function PdfPassword() {
  const [file, setFile] = useState<File | null>(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [show, setShow] = useState(false);
  const [blockPrint, setBlockPrint] = useState(false);
  const [blockCopy, setBlockCopy] = useState(false);
  const [blockEdit, setBlockEdit] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [lockedName, setLockedName] = useState<string | null>(null);
  const busy = useRef(false);
  const [batchMode, setBatchMode] = useState(false);
  const [result, setResult] = useState<{ blob: Blob; check: ProtectCheck } | null>(null);

  const clearAll = () => { setPassword(""); setConfirmPassword(""); setResult(null); setBlockPrint(false); setBlockCopy(false); setBlockEdit(false); };
  // A password belongs to one document: a new file starts clean (PWD-5).
  const handleFiles = useCallback((files: File[]) => {
    const f = files[0] || null;
    clearAll();
    // Already protected and not unlocked: say so here instead of waiting on a file that cannot be read.
    if (f && isSkippedLocked(f)) { setFile(null); setLockedName(f.name); return; }
    setLockedName(null);
    setFile(f);
  }, []);

  // Any change to the password or the options makes the file on screen stale, so it is dropped (PWD-3).
  const edit = (fn: () => void) => { fn(); setResult(null); };

  const strength = rate(password);
  const tooShort = password.length > 0 && password.length < MIN_LEN;
  const edgeSpace = password !== password.trim();
  const passwordsMatch = password.length >= MIN_LEN && password === confirmPassword;
  const showMismatch = confirmPassword.length > 0 && password !== confirmPassword;

  const protect = async () => {
    if (!file || !passwordsMatch || busy.current) return;
    busy.current = true;
    setProcessing(true);
    try {
      const blob = await protectPdfWithPassword(file, password, { restrict: { printing: blockPrint, copying: blockCopy, modifying: blockEdit } });
      const check = await verifyProtectedPdf(file, blob, password);
      if (!check.ok) {
        toast.error(`The protected file did not pass its check (${check.reason ?? "unknown problem"}), so it was not offered. Try again or tell us about it.`);
        return;
      }
      setResult({ blob, check });
      toast.success("Password protection added");
    } catch (err) {
      console.error("PDF encryption failed:", err);
      toast.error("Couldn't protect this PDF. It may be damaged; try Repair PDF first.");
    } finally {
      busy.current = false;
      setProcessing(false);
    }
  };

  const settingsBlock = (
    <div className="space-y-4">
          <div>
            <Label htmlFor="pdf-pw">Password to require</Label>
            <div className="relative">
              <Input id="pdf-pw" type={show ? "text" : "password"} autoComplete="new-password" value={password} onChange={(e) => edit(() => setPassword(e.target.value))} placeholder="Enter a password..." className="pr-10" />
              <button type="button" onClick={() => setShow((v) => !v)} aria-label={show ? "Hide passwords" : "Show passwords"} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {strength && (
              <div className="mt-1.5">
                <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                  <div className={`h-full ${strength.color} transition-all`} style={{ width: `${strength.pct}%` }} />
                </div>
                <p className="text-xs text-muted-foreground mt-0.5" role="status" aria-live="polite">Strength: {strength.label}</p>
              </div>
            )}
            {tooShort && <p className="text-xs text-destructive mt-1">Use at least {MIN_LEN} characters.</p>}
            {edgeSpace && !tooShort && <NoticeBox className="text-xs mt-1">The password starts or ends with a space. Spaces count, so you will need to type it exactly.</NoticeBox>}
          </div>

          <div>
            <Label htmlFor="pdf-pw2">Confirm password</Label>
            <Input id="pdf-pw2" type={show ? "text" : "password"} autoComplete="new-password" value={confirmPassword} onChange={(e) => edit(() => setConfirmPassword(e.target.value))} placeholder="Re-enter the same password..." />
            {showMismatch && <p className="text-xs text-destructive mt-1">Passwords don't match</p>}
          </div>

          <fieldset className="space-y-1.5">
            <legend className="text-sm font-medium">Also ask readers to block (optional)</legend>
            {([["Printing", blockPrint, setBlockPrint], ["Copying text", blockCopy, setBlockCopy], ["Editing", blockEdit, setBlockEdit]] as const).map(([label, val, set]) => (
              <label key={label} className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={val} onChange={(e) => edit(() => set(e.target.checked))} />
                {label}
              </label>
            ))}
            <p className="text-xs text-muted-foreground">Readers are asked to respect these, and most do. The password is what keeps people out.</p>
          </fieldset>

          <NoticeBox>
            <div className="flex gap-2">
              <AlertTriangle className="h-4 w-4 text-primary shrink-0 mt-0.5" />
              <p className="text-xs">
                Write this password down somewhere safe. There is no way to recover it if you forget it. Your original file is not changed.
              </p>
            </div>
          </NoticeBox>
    </div>
  );

  const batchProblem = (): string | null => (passwordsMatch ? null : "Type the password twice, the same both times, with at least " + MIN_LEN + " characters");

  return (
    <ToolContainer>
      <BatchModeToggle batch={batchMode} onChange={setBatchMode} limitText="Up to 5 files on the trial, one password for all, counts as one use." />
      {batchMode ? (
        <BatchRunner
          accept=".pdf" noun="PDF" zipName="protected-pdfs.zip" runLabel="Protect all"
          check={batchProblem}
          process={async (f) => {
            const blob = await protectPdfWithPassword(f, password, { restrict: { printing: blockPrint, copying: blockCopy, modifying: blockEdit } });
            const check = await verifyProtectedPdf(f, blob, password);
            if (!check.ok) throw new Error(`Did not pass its check (${check.reason ?? "unknown problem"})`);
            return { blob, fileName: `${stemOfPdfName(f.name)}-protected.pdf` };
          }}
        >
          {settingsBlock}
        </BatchRunner>
      ) : !file ? (
        <div className="space-y-3">
          <FileDropZone gateAtDownload keepSkippedProtectedFiles accept=".pdf" onFiles={handleFiles} label="Drop a PDF file" description="Add real password protection, entirely in your browser" />
          {lockedName && (
            <p role="alert" className="text-sm text-destructive">
              {protectedPdfNotice(lockedName, "protected with a new password")} <a href={UNLOCK_PDF_PATH} className="underline font-medium">Unlock it with Unlock PDF</a> first, then drop the unlocked copy here.
            </p>
          )}
        </div>
      ) : (
        <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); void protect(); }}>
          <div className="p-4 rounded-lg border bg-card">
            <p className="text-sm font-medium">{file.name}</p>
            <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
          </div>

          {settingsBlock}

          {result && (
            <NoticeBox className="flex items-start gap-2" role="status">
              <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <p className="text-sm">
                Checked: opens with your password · {result.check.pages} {result.check.pages === 1 ? "page" : "pages"} · {formatFileSize(result.blob.size)} · AES-256
              </p>
            </NoticeBox>
          )}

          <div className="flex gap-3">
            {result ? (
              <>
                <DownloadButton blob={result.blob} fileName={`${stemOfPdfName(file.name)}-protected.pdf`} />
                <Button type="button" variant="outline" onClick={() => { setFile(null); clearAll(); }}>New</Button>
              </>
            ) : processing ? (
              <ProcessingState />
            ) : (
              <>
                <Button type="submit" disabled={!passwordsMatch}>Protect PDF</Button>
                <Button type="button" variant="ghost" onClick={() => { setFile(null); clearAll(); }}>Reset</Button>
              </>
            )}
          </div>
        </form>
      )}
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
